import * as Sentry from '@sentry/browser';
import { buildURL, fetchRequest } from 'Utils/apiFetchRequest';
import createVideoItem, { markVideoItemAsNew } from 'Utils/videoItem';

const __CONTEXT__ = {
    longThumbnail: window.__VUMEDI_TERM_DETAIL_CONTEXT__.static.longThumbnail,
    browseSlug: window.__VUMEDI_TERM_DETAIL_CONTEXT__.browseSlug,
    viewedUUID: window.__VUMEDI_TERM_DETAIL_CONTEXT__.viewedUUID,
};

class LoadingController {
    constructor(parentSelector) {
        this.loadingRoot = document.querySelector(`${parentSelector} .js_loading`);
        this.start();
    }

    start() {
        this.loadingRoot.classList.remove('hidden');
    }

    stop() {
        this.loadingRoot.classList.add('hidden');
    }
}

class VideoList {
    constructor(rootSelector, requestBase) {
        this.requestBase = requestBase;
        this.root = document.querySelector(rootSelector);
        this.loadingController = new LoadingController(rootSelector);
        this.messageContainer = this.root.querySelector('.js_message');
    }

    getVideos(params) {
        return new Promise((resolve, reject) => {
            fetchRequest(buildURL(this.requestBase, params))
                .then((data) => {
                    if (!data?.results?.length) {
                        reject(new Error(`${this.requestBase}: empty payload`));
                        return;
                    }

                    resolve({
                        results: data.results,
                        count: data.count,
                    });
                })
                .catch(() => reject(new Error(`${this.requestBase}: API error`)));
        });
    }

    load(params) {
        return new Promise((resolve) => {
            this.clear();
            this.loadingController.start();
            this.messageContainer.innerText = '';

            this.getVideos(params)
                .then((data) => {
                    this.render(data.results);
                    resolve(data.count);
                })
                .catch((error) => {
                    let message;

                    if (error.message.includes('empty payload')) {
                        message = 'There are no available videos.';
                    } else {
                        Sentry.captureException(error);
                        message = 'Ops, something went wrong.';
                    }

                    this.messageContainer.innerText = message;
                    resolve(0);
                })
                .finally(() => {
                    this.loadingController.stop();
                });
        });
    }

    clear() {
        const videos = this.root.querySelectorAll('.disease-column-page__video');

        videos?.forEach((el) => {
            this.root.removeChild(el);
        });
    }

    render(videos) {
        for (const video of videos) {
            const videoItem = createVideoItem('long', video, {
                long: {
                    thumbnailPlaceholder: __CONTEXT__.longThumbnail,
                    includeFeaturing: true,
                    includeThumbups: true,
                    showAuthor: true,
                },
            });

            if (videoItem) {
                const videoContent = document.createElement('div');
                videoContent.classList.add('disease-column-page__video');
                videoContent.appendChild(videoItem);
                this.root.appendChild(videoContent);
            }
        }
    }
}

class Pagination {
    constructor(rootSelector) {
        this.root = document.querySelector(rootSelector);
        this.prevButton = this.root.querySelector('.js_paginationPrev');
        this.nextButton = this.root.querySelector('.js_paginationNext');
        this.pagesContainer = this.root.querySelector('.js_paginationPages');

        this.state = {
            limit: 0,
            page: 0,
            count: 0,
        };

        /** Callback that's called on every button click, (page) => {} */
        this.onClick = null;

        /** Total number of page buttons in pagination, not including prev/next buttons */
        this.countPageButtons = 5;

        this._registerListeners();
    }

    _registerListeners() {
        this.prevButton.addEventListener('click', (ev) => {
            ev.preventDefault();

            if (this.state.page === 1) {
                return;
            }

            this._reportClick(--this.state.page);
        });

        this.nextButton.addEventListener('click', (ev) => {
            ev.preventDefault();

            if (this.state.page === Math.ceil(this.state.count / this.state.limit)) {
                return;
            }

            this._reportClick(++this.state.page);
        });

        this.pagesContainer.addEventListener('click', (ev) => {
            ev.preventDefault();

            if (!ev.target.dataset?.pageIndex) {
                return;
            }

            this._reportClick(parseInt(ev.target.dataset.pageIndex, 10));
        });
    }

    _createPageButton(index) {
        const button = document.createElement('button');

        button.innerText = index;
        button.dataset.pageIndex = index;
        button.classList.add('vm-button', 'vm-button--sm', 'vm-button--tertiary', 'vm-button--pagination');

        if (index === this.state.page) {
            button.setAttribute('disabled', 'disabled');
        }

        return button;
    }

    _reportClick(page) {
        if (typeof this.onClick === 'function') {
            this.onClick(page);
        }
    }

    hide() {
        this.root.classList.add('hidden');
    }

    /**
     *
     * @param { limit, page, count } state
     */
    update(state) {
        this.state = state;

        if (this.state.count <= this.state.limit) {
            this.root.classList.add('hidden');
            return;
        }

        this.root.classList.remove('hidden');

        if (this.state.page === 1) {
            this.prevButton.setAttribute('disabled', 'disabled');
        } else {
            this.prevButton.removeAttribute('disabled');
        }

        if (this.state.page === Math.ceil(this.state.count / this.state.limit)) {
            this.nextButton.setAttribute('disabled', 'disabled');
        } else {
            this.nextButton.removeAttribute('disabled');
        }

        while (this.pagesContainer.firstChild) {
            this.pagesContainer.removeChild(this.pagesContainer.firstChild);
        }

        const lowerLimit = Math.max(this.state.page - Math.floor(this.countPageButtons / 2), 1);
        const upperLimit = Math.min(
            Math.max(this.state.page + Math.floor(this.countPageButtons / 2), this.countPageButtons),
            Math.ceil(this.state.count / this.state.limit),
        );

        for (let i = lowerLimit; i <= upperLimit; ++i) {
            this.pagesContainer.appendChild(this._createPageButton(i));
        }

        this.root.classList.remove('hidden');
    }
}

function main() {
    const state = (() => {
        const url = new URL(window.location.href);

        return {
            count: 0,
            filter: url.searchParams.get('filter') || null,
            limit: 25,
            page: parseInt(url.searchParams.get('page'), 10) || 1,
            sort: url.searchParams.get('sort') || '-created',
            highlights: [],
        };
    })();

    const dom = {
        filtersContainer: document.querySelector('.js_filtersContainer'),
        filtersClear: document.querySelector('.js_clearFilters'),
        filtersHide: document.querySelector('.js_hideFilters'),
        filtersShow: document.querySelector('.js_showFilters'),
        filterOptions: document.querySelectorAll('[name="presentation-type"]'),
        sortActive: document.querySelector('.js_sortActive'),
        sortOptions: document.querySelectorAll('.js_sortOption'),
        videos: document.querySelectorAll('.js_video'),
        title: document.querySelector('.js_title'),
        keyPracticeSection: document.querySelector('.js_keyVideosSection'),
    };

    const videoList = new VideoList('.js_videosList', '/api/videos/');
    const keyVideoList = dom.keyPracticeSection ? new VideoList('.js_keyVideosList', '/api/videos/') : null;
    const pagination = new Pagination('.js_pagination');

    const hideFilters = (ev) => {
        ev.preventDefault?.();
        dom.filtersContainer.classList.remove('visible');
        document.body.style.overflow = '';
    };

    const showFilters = (ev) => {
        ev.preventDefault?.();
        dom.filtersContainer.classList.add('visible');
        document.body.style.overflow = 'hidden';
    };

    const setActiveSortTitle = () => {
        const sortOption = Array.from(dom.sortOptions).find((el) => el.dataset.param === state.sort);
        dom.sortActive.innerText = sortOption.innerText;
    };

    const setURLParameters = () => {
        const url = new URL(window.location.href);
        let changed = false;

        if (url.searchParams.get('filter') !== state.filter) {
            if (state.filter === null) {
                url.searchParams.delete('filter');
            } else {
                url.searchParams.set('filter', state.filter);
            }

            changed = true;
        }

        if (url.searchParams.get('sort') !== state.sort) {
            url.searchParams.set('sort', state.sort);
            changed = true;
        }

        if (url.searchParams.get('page') !== state.page) {
            url.searchParams.set('page', state.page);
            changed = true;
        }

        if (changed) {
            window.history.replaceState({}, '', url);
        }
    };

    const loadVideos = () => {
        setURLParameters();

        const params = {
            limit: state.limit,
            offset: state.limit * (state.page - 1),
            learning_journey_browse: __CONTEXT__.browseSlug,
            ordering: state.sort,
        };

        if (state.filter) {
            // We need to use filter name when sending an API request, slug doesn't exist in the database
            params.learning_journey_step = encodeURIComponent(dom.title.innerText);
        }

        pagination.hide();
        videoList.load(params).then((count) => {
            state.count = count;
            pagination.update({
                limit: state.limit,
                page: state.page,
                count: state.count,
            });
        });
    };

    const retrieveKeyPracticeHighlights = () =>
        new Promise((resolve) => {
            const params = {};

            if (__CONTEXT__.viewedUUID) {
                params.viewed_uuid = __CONTEXT__.viewedUUID;
            }

            fetchRequest(buildURL('/api/highlighted-disease-pages/', params))
                .then((data) => {
                    if (!data.results?.length) {
                        resolve();
                        return;
                    }

                    const term = data.results.find((el) => el.url === window.location.pathname);
                    state.highlights = term.unseen_key_practice_update_videos;
                    resolve();
                })
                .catch((error) => {
                    Sentry.captureException(error);
                    resolve();
                });
        });

    const loadKeyPracticeUpdateVideos = () => {
        if (!dom.keyPracticeSection) {
            return;
        }

        dom.keyPracticeSection.classList.remove('hidden');
        keyVideoList
            .load({
                learning_journey_browse: __CONTEXT__.browseSlug,
                key_practice_update: true,
                ordering: '-created',
            })
            .then(() => {
                if (!state.highlights?.length) {
                    return;
                }

                for (const video of state.highlights) {
                    const videoElement = document.querySelector(`.video-item[data-id="${video.id}"]`);

                    if (videoElement) {
                        markVideoItemAsNew(videoElement);
                    }
                }
            });
    };

    const hideKeyPracticeUpdateVideos = () => {
        if (!dom.keyPracticeSection) {
            return;
        }

        dom.keyPracticeSection.classList.add('hidden');
    };

    const handleFilterChange = (ev) => {
        const targetId = ev.target.getAttribute('id') ?? null;

        dom.title.innerText = ev.target.value || ev.target.dataset.value;

        if (targetId === null) {
            dom.filtersClear.classList.add('vm-button--hidden');
            dom.filterOptions.forEach((el) => {
                el.checked = false;
            });
        } else {
            dom.filtersClear.classList.remove('vm-button--hidden');
        }

        hideFilters({});
        window.scrollTo({ top: 0, left: 0, behavior: 'smooth' });

        state.filter = targetId;
        state.page = 1;

        loadVideos();

        if (state.page === 1 && state.filter === null) {
            loadKeyPracticeUpdateVideos();
        } else {
            hideKeyPracticeUpdateVideos();
        }
    };

    const handleSortChange = (ev) => {
        ev.preventDefault();

        state.page = 1;
        state.sort = ev.target.dataset.param;

        setActiveSortTitle();
        loadVideos();
    };

    const initialize = () => {
        const initialFilter = Array.from(dom.filterOptions).find((el) => el.getAttribute('id') === state.filter);
        const initialPage = state.page;
        const initialSort = Array.from(dom.sortOptions).find((el) => el.dataset.param === state.sort);

        if (initialFilter) {
            initialFilter.setAttribute('checked', true);
            dom.title.innerText = initialFilter.value;
            dom.filtersClear.classList.remove('vm-button--hidden');
        }

        if (initialPage < 1) {
            state.page = 1;
        }

        if (!initialSort) {
            state.sort = '-created';
        }

        setActiveSortTitle();
        loadVideos();

        if (dom.keyPracticeSection && state.page === 1 && !initialFilter) {
            loadKeyPracticeUpdateVideos();
        }

        window.addEventListener('resize', () => {
            if (window.innerWidth > 991) {
                hideFilters({});
            }
        });
    };

    dom.filtersHide.addEventListener('click', hideFilters);
    dom.filtersShow.addEventListener('click', showFilters);
    dom.filtersClear.addEventListener('click', () => handleFilterChange({ target: dom.filtersClear }));
    dom.filterOptions.forEach((el) => el.addEventListener('change', handleFilterChange));
    dom.sortOptions.forEach((el) => el.addEventListener('click', handleSortChange));

    pagination.onClick = (page) => {
        state.page = page;
        loadVideos();

        if (!dom.keyPracticeSection) {
            return;
        }

        if (page === 1) {
            loadKeyPracticeUpdateVideos();
        } else {
            hideKeyPracticeUpdateVideos();
        }
    };

    if (dom.keyPracticeSection) {
        retrieveKeyPracticeHighlights().then(initialize);
    } else {
        initialize();
    }
}

window.addEventListener('DOMContentLoaded', main);
